/**
 * CaretAnimationSystem - Clean, efficient caret blinking
 * 
 * Provides simple blinking functionality without React loops or performance issues:
 * - Pure JavaScript interval management
 * - Direct canvas manipulation for performance
 * - Focus-aware blinking (stops when document loses focus)
 * - Typing-aware blinking (pauses during typing)
 * - Clean cleanup and memory management
 * - MS Word timing (530ms interval)
 */

export interface BlinkingConfig {
  enabled: boolean;
  interval: number;              // Blink interval in milliseconds (MS Word: 530ms)
  pauseOnTyping: boolean;        // Pause blinking while user is typing
  stopOnBlur: boolean;           // Stop blinking when document loses focus
  debugMode: boolean;            // Enable debug logging
}

export interface BlinkingState {
  isVisible: boolean;
  isBlinking: boolean;
  isPaused: boolean;
  lastTypingTime: number;
}

export class CaretAnimationSystem {
  private config: BlinkingConfig;
  private state: BlinkingState;
  private blinkInterval: number | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private context: CanvasRenderingContext2D | null = null;
  private currentPosition: { x: number; y: number; height: number } | null = null;
  
  // Performance tracking
  private blinkCount: number = 0;
  private lastBlinkTime: number = 0;
  
  // Event listeners
  private typingTimeout: number | null = null;
  private focusHandler: () => void;
  private blurHandler: () => void;
  private keydownHandler: (e: KeyboardEvent) => void;
  
  constructor(config: Partial<BlinkingConfig> = {}) {
    this.config = {
      enabled: true,
      interval: 530, // MS Word standard
      pauseOnTyping: true,
      stopOnBlur: true,
      debugMode: false,
      ...config
    };
    
    this.state = {
      isVisible: true,
      isBlinking: false,
      isPaused: false,
      lastTypingTime: 0
    };
    
    // Bind event handlers
    this.focusHandler = this.handleFocus.bind(this);
    this.blurHandler = this.handleBlur.bind(this);
    this.keydownHandler = this.handleKeydown.bind(this);
    
    this.setupEventListeners();
  }
  
  /**
   * Initialize the blinking system with canvas and position
   */
  initialize(canvas: HTMLCanvasElement, position: { x: number; y: number; height: number }): void {
    this.canvas = canvas;
    this.context = canvas.getContext('2d');
    this.currentPosition = position;
    
    if (this.config.debugMode) {
      console.log('[CaretAnimationSystem] Initialized:', {
        canvas: !!canvas,
        context: !!this.context,
        position: this.currentPosition,
        config: this.config
      });
    }
    
    if (this.config.enabled) {
      this.startBlinking();
    }
  }
  
  /**
   * Start blinking animation
   */
  startBlinking(): void {
    if (this.blinkInterval || !this.config.enabled) {
      return; // Already blinking or disabled
    }
    
    this.state.isBlinking = true;
    this.state.isVisible = true;
    
    this.blinkInterval = window.setInterval(() => {
      this.performBlink();
    }, this.config.interval);
    
    if (this.config.debugMode) {
      console.log('[CaretAnimationSystem] Blinking started:', {
        interval: this.config.interval,
        state: this.state
      });
    }
  }
  
  /**
   * Stop blinking animation
   */
  stopBlinking(): void {
    if (this.blinkInterval) {
      clearInterval(this.blinkInterval);
      this.blinkInterval = null;
    }
    
    this.state.isBlinking = false;
    this.state.isVisible = true; // Always visible when not blinking
    
    if (this.config.debugMode) {
      console.log('[CaretAnimationSystem] Blinking stopped:', {
        blinkCount: this.blinkCount,
        state: this.state
      });
    }
  }
  
  /**
   * Update caret position
   */
  updatePosition(position: { x: number; y: number; height: number }): void {
    this.currentPosition = position;
    
    // Resume blinking if it was paused
    if (this.state.isPaused && this.config.pauseOnTyping) {
      this.resumeBlinking();
    }
  }
  
  /**
   * Pause blinking (e.g., during typing)
   */
  pauseBlinking(): void {
    if (!this.config.pauseOnTyping) return;
    
    this.state.isPaused = true;
    this.state.isVisible = true; // Keep visible when paused
    
    if (this.config.debugMode) {
      console.log('[CaretAnimationSystem] Blinking paused');
    }
  }
  
  /**
   * Resume blinking after pause
   */
  resumeBlinking(): void {
    if (!this.config.pauseOnTyping) return;
    
    this.state.isPaused = false;
    
    if (this.config.debugMode) {
      console.log('[CaretAnimationSystem] Blinking resumed');
    }
  }
  
  /**
   * Get current blinking state
   */
  getState(): BlinkingState {
    return { ...this.state };
  }
  
  /**
   * Check if caret should be visible
   */
  shouldShowCaret(): boolean {
    if (!this.state.isBlinking) {
      return true; // Always visible when not blinking
    }
    
    if (this.state.isPaused) {
      return true; // Always visible when paused
    }
    
    return this.state.isVisible;
  }
  
  /**
   * Cleanup and destroy the animation system
   */
  destroy(): void {
    this.stopBlinking();
    this.removeEventListeners();
    
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }
    
    this.canvas = null;
    this.context = null;
    this.currentPosition = null;
    
    if (this.config.debugMode) {
      console.log('[CaretAnimationSystem] Destroyed:', {
        totalBlinks: this.blinkCount
      });
    }
  }
  
  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================
  
  /**
   * Perform a single blink cycle
   */
  private performBlink(): void {
    // Skip blink if paused
    if (this.state.isPaused) {
      return;
    }
    
    // Skip blink if document doesn't have focus
    if (this.config.stopOnBlur && !document.hasFocus()) {
      return;
    }
    
    // Toggle visibility
    this.state.isVisible = !this.state.isVisible;
    this.blinkCount++;
    this.lastBlinkTime = performance.now();
    
    // Trigger redraw if we have a canvas
    if (this.canvas && this.context && this.currentPosition) {
      this.redrawCaret();
    }
    
    if (this.config.debugMode && this.blinkCount % 10 === 0) {
      console.log('[CaretAnimationSystem] Blink cycle:', {
        blinkCount: this.blinkCount,
        isVisible: this.state.isVisible,
        isPaused: this.state.isPaused
      });
    }
  }
  
  /**
   * Redraw the caret with current visibility state
   */
  private redrawCaret(): void {
    if (!this.context || !this.currentPosition) return;
    
    const { x, y, height } = this.currentPosition;
    const width = 1; // Thin line like MS Word
    
    // Clear the caret area
    this.context.clearRect(x - 1, y, width + 2, height);
    
    // Draw caret if visible
    if (this.state.isVisible) {
      this.context.fillStyle = '#000000';
      this.context.fillRect(x, y, width, height);
    }
  }
  
  /**
   * Setup event listeners for focus and typing detection
   */
  private setupEventListeners(): void {
    if (this.config.stopOnBlur) {
      window.addEventListener('focus', this.focusHandler);
      window.addEventListener('blur', this.blurHandler);
    }
    
    if (this.config.pauseOnTyping) {
      document.addEventListener('keydown', this.keydownHandler);
    }
  }
  
  /**
   * Remove event listeners
   */
  private removeEventListeners(): void {
    window.removeEventListener('focus', this.focusHandler);
    window.removeEventListener('blur', this.blurHandler);
    document.removeEventListener('keydown', this.keydownHandler);
  }
  
  /**
   * Handle document focus
   */
  private handleFocus(): void {
    if (this.config.enabled && !this.state.isBlinking) {
      this.startBlinking();
    }
  }
  
  /**
   * Handle document blur
   */
  private handleBlur(): void {
    if (this.config.stopOnBlur) {
      this.stopBlinking();
    }
  }
  
  /**
   * Handle keydown events for typing detection
   */
  private handleKeydown(e: KeyboardEvent): void {
    // Only pause for actual typing keys (not navigation keys)
    const typingKeys = [
      'Backspace', 'Delete', 'Enter', 'Tab',
      'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
      'Home', 'End', 'PageUp', 'PageDown'
    ];
    
    if (!typingKeys.includes(e.key)) {
      this.pauseBlinking();
      
      // Resume blinking after a short delay
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
      }
      
      this.typingTimeout = window.setTimeout(() => {
        this.resumeBlinking();
      }, 1000); // Resume after 1 second of no typing
    }
  }
}

// Export default configuration for easy setup
export const DEFAULT_BLINKING_CONFIG: BlinkingConfig = {
  enabled: true,
  interval: 530, // MS Word standard
  pauseOnTyping: true,
  stopOnBlur: true,
  debugMode: false
};









